import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SITE, IMG } from "../data/siteData";

const programs = [
  {
    name: "Board & Train",
    to: "/services/board-and-train",
    img: IMG.boardTrain,
    tag: "Most Immersive",
    price: "From $1,950",
    desc: "Your dog stays with us for 2-6+ weeks of round-the-clock professional training at our facility just outside Calgary.",
  },
  {
    name: "Private Training",
    to: "/services/private-training",
    img: IMG.privateTraining,
    tag: "Most Hands-On",
    price: "$1,200 · 6 sessions",
    desc: "One-on-one lessons where you learn alongside your dog, with a plan customized to your exact goals. Virtual lessons available at $200/hour.",
  },
  {
    name: "Jumpstart Program",
    to: "/services/jumpstart",
    img: IMG.jumpstart,
    tag: "For Young Dogs",
    price: "$1,950",
    desc: "A 2-week board and train foundation for puppies and young dogs aged 4 months to 1 year.",
  },
  {
    name: "Virtual Lessons",
    to: "/virtual-dog-training",
    img: IMG.virtualLessons,
    tag: "Anywhere in Canada",
    price: "$200/hour",
    desc: "Live one-on-one video coaching with a certified trainer — expert guidance from your living room, no travel required.",
  },
];

const rows = [
  { label: "Investment", values: ["$1,950 – $6,000 (by program length)", "$1,200 (6 sessions) · Virtual $200/hr", "$1,950 (2 weeks)", "$200 / hour session"] },
  { label: "Best for", values: ["Any dog, any issue — obedience to severe behaviour modification", "Owners who want to train hands-on with coaching", "Puppies & young dogs (4 mo – 1 yr)", "Owners anywhere in Canada who want live coaching from home"] },
  { label: "Format", values: ["Dog stays at our facility (2-6+ weeks)", "Series of one-on-one lessons with you and your dog", "Dog stays at our facility (2 weeks)", "Live one-hour video sessions"] },
  { label: "On & off-leash obedience", values: [true, true, false, true] },
  { label: "Behaviour modification (aggression, anxiety, reactivity)", values: [true, true, false, true] },
  { label: "Socialization & new-environment exposure", values: [true, false, true, false] },
  { label: "Owner education sessions included", values: [true, true, true, true] },
  { label: "Go-home guide & training equipment", values: [true, false, true, false] },
  { label: "Lifetime phone/text/email support", values: [true, true, true, true] },
  { label: "Graduate boarding discounts", values: [true, false, true, false] },
];

const Cell = ({ v }) =>
  v === true ? <Check size={18} className="text-brand mx-auto" data-testid="check-cell" /> :
  v === false ? <X size={16} className="text-white/25 mx-auto" data-testid="x-cell" /> :
  <span className="text-sm text-white/75 leading-snug block">{v}</span>;

export default function Programs() {
  return (
    <div data-testid="programs-page">
      <SEO
        title="All Training Programs Compared | Chinook K9 Calgary"
        description="Compare Chinook K9's dog training programs side by side: Board & Train, Private Training and the Puppy Jumpstart Program. Serving Calgary & surrounding areas."
        path="/programs"
      />

      <section className="bg-obsidian pt-40 pb-16 border-b border-white/10" data-testid="programs-hero">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4">All Programs</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl">
              Find the right program for your dog
            </h1>
            <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
              Every dog and every goal is different. Compare our three certified training programs side by side —
              then tell us about your dog and we'll confirm the best fit.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20" data-testid="programs-cards">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-8" data-testid="programs-cards-grid">
          {programs.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <Link to={p.to} data-testid={`programs-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group block relative rounded-2xl overflow-hidden h-[340px] border border-white/10">
                <img src={p.img} alt={`${p.name} at Chinook K9`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
                <span className="absolute top-5 left-5 bg-brand text-white text-[11px] font-heading font-bold tracking-widest uppercase px-3.5 py-1.5 rounded-full">{p.tag}</span>
                <span className="absolute top-5 right-5 bg-black/70 text-white text-xs font-heading font-bold px-3.5 py-1.5 rounded-full border border-white/20">{p.price}</span>
                <div className="absolute bottom-0 p-7">
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{p.name}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{p.desc}</p>
                  <span className="inline-flex items-center gap-2 mt-4 bg-brand text-white font-heading font-bold text-sm px-6 py-2.5 rounded-full group-hover:bg-red-700 group-hover:gap-3 transition-all shadow-[0_0_15px_rgba(220,38,38,0.35)]">
                    Full Details <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0D0D0D] border-y border-white/10 py-20" data-testid="programs-compare">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">Side-by-side comparison</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="programs-table">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="bg-[#161616] border-b border-white/10">
                    <th className="px-6 py-5 text-xs font-heading font-bold tracking-widest uppercase text-white/60 w-[34%]">What's included</th>
                    {programs.map((p) => (
                      <th key={p.name} className="px-6 py-5 text-center">
                        <Link to={p.to} className="font-heading font-bold text-white hover:text-brand transition-colors">{p.name}</Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.label} className={`border-b border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`} data-testid={`compare-row-${i}`}>
                      <td className="px-6 py-4 text-sm font-semibold text-white/90">{r.label}</td>
                      {r.values.map((v, j) => (
                        <td key={j} className="px-6 py-4 text-center">{<Cell v={v} />}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20" data-testid="programs-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Not sure which fits?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Tell us about your dog and your goals — we'll recommend the right program, no pressure.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="programs-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Start Today! <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} data-testid="programs-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <Phone size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
